<template>
  <div class="table-container">
    <GridRow>
      <GridCol :sm="12" style="margin-top: 16px; margin-bottom: 12px">
        <slot v-if="showColPicker" name="col-picker-extras" />
        <div class="title-btns">
          <div class="table-headings">
            <h4>{{ tableTitle }}</h4>
            <p>{{ tableSubtitle }}</p>
          </div>
          <div class="table-btns">
            <div
              :class="{
                'column-toggle': true,
                'filter-visible': colsPickerVisible,
                'filter-hidden': !colsPickerVisible,
              }"
              @click="toggleColumnVisibility"
              @keyup.enter="toggleColumnVisibility"
            >
              <span class="column-btn" style="padding-left: 0">
                <i class="fa fa-columns" />
              </span>
              <p>{{ colsPickerVisible ? 'Hide Columns' : 'Select Columns' }}</p>
            </div>

            <div
              :class="{
                'column-toggle': true,
                'filter-visible': filterView,
                'filter-hidden': !filterView,
              }"
              @click="toggleFilterVisibility"
              @keyup.enter="toggleFilterVisibility"
            >
              <span v-if="shouldDisplayFilters" class="column-btn" style="padding-left: 0">
                <i class="fa fa-filter" />
              </span>
              <p>{{ filterView ? 'Hide Filters' : 'Set Filters' }}</p>
            </div>
          </div>
        </div>
        <template v-if="showColPicker">
          <span v-if="colsPickerVisible" style="padding-top: 12px">
            <a
              class="btn btn-link"
              style="padding-left: 0"
              @click="toggleColumnVisibility"
              @keyup.enter="toggleColumnVisibility"
            >
              <b>&times; <i class="fa fa-columns" /> Columns:</b> </a
            >{{ ' ' }}
            <span
              v-for="({ id, title, groupTitle, tooltip }, idx) in toggleableHeadings"
              :key="'tablectrl-' + id"
            >
              <a
                v-tooltip="tooltip"
                :class="{ badge: true, 'badge-enabled': showCols.includes(id) }"
                @click="toggleCol(id)"
                @keyup.enter="toggleCol(id)"
              >
                <i v-if="showCols.includes(id)" class="glyphicon glyphicon-check" />
                <i v-else class="glyphicon glyphicon-plus" />
                {{ groupTitle || title }}
              </a>
              <span v-if="idx < toggleableHeadings.length - 1"> | </span>
            </span>
            &raquo;
            <a class="badge" @click="setAllCols" @keyup.enter="setAllCols">
              <i class="glyphicon glyphicon-check" /> Show all</a
            >
            |
            <a class="badge" @click="resetCols" @keyup.enter="resetCols">
              <i class="glyphicon glyphicon-alert" /> Reset columns</a
            >
          </span>
        </template>
        <br v-if="showColPicker && colsPickerVisible" />
      </GridCol>
    </GridRow>
    <div class="bts-plus-table">
      <div class="tr-con">
        <TableControls
          v-if="!nearby && topControls"
          :page-size.sync="pageSize"
          :page.sync="page"
          :num-entries-filtered="sortedResults.length"
          :num-entries="dataCount"
          :loading="loading"
          :more="more"
          :is-any-filter-applied="isAnyFilterApplied"
          @download-more="$emit('download-more', $event)"
          @download-cancel="$emit('download-cancel', $event)"
        >
          <slot name="extra-controls" />
          <a
            class="btn btn-link"
            @click.stop="checkDownloadCSV()"
            @keyup.enter="checkDownloadCSV()"
          >
            <i class="glyphicon glyphicon-download-alt" />
            Download as CSV
          </a>
        </TableControls>
        <div class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
              <tr class="columns-row">
                <th
                  v-for="{
                    id,
                    title,
                    groupTitle,
                    groupId = null,
                    sortable = true,
                    right = false,
                    children = null,
                    groupClass = {},
                    tooltip = null,
                    thClass = {},
                    hideTitle = false,
                    type,
                  } in showGroupHeadings"
                  :key="id"
                  v-tooltip="tooltip"
                  :class="{
                    'text-right': !groupId && right,
                    'text-center': groupId,
                    'th-w-rowspan': hasGroupHeadings && !groupId,
                    ...groupClass,
                    ...(!groupId ? thClass : {}),
                  }"
                  :colspan="children && children.length ? children.length : null"
                  :rowspan="hasGroupHeadings && !groupId ? 2 : null"
                >
                  <slot :name="'header-' + type">
                    <a
                      v-if="sortable && !groupId"
                      class="sorter column-sort"
                      @click.stop="onSortClick(id)"
                      @keyup.enter="onSortClick(id)"
                    >
                      <span class="filter-label">
                        {{ groupTitle || title }}
                      </span>
                      <b v-if="currentSortBy === id">
                        <i
                          v-if="currentReversed"
                          class="glyphicon glyphicon-sort-by-attributes-alt active"
                        />
                        <i v-else class="glyphicon glyphicon-sort-by-attributes active" />
                      </b>
                      <i v-else class="glyphicon glyphicon-sort-by-attributes" />
                    </a>
                    <span v-else-if="sortable && groupId" class="filter-label">
                      {{ groupTitle || title }}
                    </span>
                    <span v-else-if="!hideTitle" class="filter-label">
                      {{ groupTitle || title }}
                    </span>
                  </slot>
                </th>
              </tr>
              <tr v-if="hasGroupHeadings" class="sort-tr">
                <th
                  v-for="{
                    id,
                    title,
                    sortable = true,
                    right = false,
                    thClass = {},
                  } in showSubHeadings"
                  :key="id"
                  :class="{ 'text-right': right, ...thClass }"
                >
                  <a
                    v-if="sortable"
                    class="sorter"
                    @click.stop="onSortClick(id)"
                    @keyup.enter="onSortClick(id)"
                  >
                    <span>
                      {{ title }}
                    </span>
                    <b v-if="currentSortBy === id">
                      <i
                        v-if="currentReversed"
                        class="glyphicon glyphicon-sort-by-attributes-alt active"
                      />
                      <i v-else class="glyphicon glyphicon-sort-by-attributes active" />
                    </b>
                    <i v-else class="glyphicon glyphicon-sort-by-attributes" />
                  </a>
                </th>
              </tr>
              <tr v-if="filterView && !nearby && shouldDisplayFilters" class="filters-row">
                <th
                  v-for="{
                    id,
                    filterable,
                    numeric,
                    right = false,
                    numericTerms = ['Fastest', 'Slowest'],
                    children = null,
                    groupId = null,
                    groupClass = {},
                  } in showGroupHeadings"
                  :key="'filter-' + id"
                  :class="{
                    'text-right': !groupId && right,
                    'text-center': groupId,
                    ...groupClass,
                  }"
                  :colspan="children && children.length ? children.length : null"
                >
                  <Select2
                    v-if="filterable && !numeric"
                    v-model="filters[id]"
                    :options="filterOpts[id]"
                    :settings="{
                      multiple: true,
                      allowClear: true,
                      placeholder: 'Filter...',
                    }"
                  />
                  <Select2
                    v-if="filterable && numeric"
                    v-model="numericFilters[id]"
                    class="filter"
                    :options="[
                      { id: '>p99', text: numericTerms[1] + ' 1%' },
                      { id: '>p95', text: numericTerms[1] + ' 5%' },
                      { id: '>p75', text: numericTerms[1] + ' 25%' },
                      { id: 'mid', text: 'Middle 50%' },
                      { id: '<p25', text: numericTerms[0] + ' 25%' },
                      { id: '<p05', text: numericTerms[0] + ' 5%' },
                      { id: '<p01', text: numericTerms[0] + ' 1%' },
                    ]"
                    :settings="{
                      multiple: false,
                      allowClear: true,
                      placeholder: 'Filter...',
                    }"
                  />
                </th>
              </tr>
            </thead>
            <tbody v-if="sortedResults.length">
              <tr v-if="timeRangeId" class="time-range-row">
                <td v-if="nearby" :colspan="showHeadings.length">
                  <slot name="extra-controls" />
                </td>
                <td v-else :colspan="showHeadings.length">
                  Showing entries from
                  <LocalDateTime :datetime="startTimeRange" /> to
                  <LocalDateTime :datetime="endTimeRange" />.
                  <span v-if="more">
                    <a
                      @click.stop="$emit('download-more', true)"
                      @keyup.enter="$emit('download-more', true)"
                      >Load older results...</a
                    >
                  </span>
                  <a
                    v-if="loading"
                    aria-label="Loading... click to cancel"
                    @click.stop="$emit('download-cancel')"
                    @keyup.enter="$emit('download-cancel')"
                    ><LoadingAnim tag="span" inline
                  /></a>
                </td>
              </tr>
              <template v-for="(row, idx) in sortedPagedData">
                <tr
                  :key="row.id + '-' + idx"
                  class="clickable-row"
                  :class="{ 'expanded-row': isExpanded(row.id) }"
                  @click.stop="setExpanded(row.id, !isExpanded(row.id))"
                  @keyup.enter="setExpanded(row.id, !isExpanded(row.id))"
                >
                  <td
                    v-for="column in showHeadings"
                    :key="'row-' + row.id + '-' + idx + '-' + column.id"
                    :class="{
                      'text-right': column.right,
                      compact: !column.notCompact,
                      ...(column.tdClass || {}),
                    }"
                  >
                    <slot
                      v-if="column.type === 'expand'"
                      name="expand"
                      :row="row"
                      :value="valueFor(row, column.id)"
                      :sorted-paged-data="sortedPagedData"
                      :expanded="isExpanded(row.id)"
                    >
                      <a
                        v-if="isExpanded(row.id)"
                        v-tooltip="'Hide extra details'"
                        aria-label="Hide extra details"
                        @click.stop="setExpanded(row.id, false)"
                        @keyup.enter="setExpanded(row.id, false)"
                      >
                        <i class="glyphicon glyphicon-resize-small" />
                      </a>
                      <a
                        v-else
                        v-tooltip="'Show extra details'"
                        aria-label="Show extra details"
                        @click.stop="setExpanded(row.id, true)"
                        @keyup.enter="setExpanded(row.id, true)"
                      >
                        <i class="glyphicon glyphicon-resize-full" />
                      </a>
                    </slot>
                    <slot
                      v-else
                      :name="column.type"
                      :column="column"
                      :row="row"
                      :value="valueFor(row, column.id)"
                      :sorted-paged-data="sortedPagedData"
                      :idx="idx"
                    >
                      <slot
                        name="default-td"
                        :column="column"
                        :row="row"
                        :value="valueFor(row, column.id)"
                        :sorted-paged-data="sortedPagedData"
                        :idx="idx"
                      >
                        <span
                          v-tooltip="column.textFn ? column.textFn(row) : valueFor(row, column.id)"
                        >
                          {{ column.textFn ? column.textFn(row) : valueFor(row, column.id) }}
                        </span>
                      </slot>
                    </slot>
                  </td>
                </tr>
                <transition :key="'expanded-' + row.id" name="expand">
                  <tr v-if="isExpanded(row.id)" class="expanded-row">
                    <td :colspan="showHeadings.length">
                      <div class="td-content">
                        <slot name="expanded" :row="row">
                          <code>{{ row }}</code>
                        </slot>
                      </div>
                    </td>
                  </tr>
                </transition>
              </template>
              <tr v-if="timeRangeId" class="time-range-row">
                <td v-if="nearby" :colspan="showHeadings.length">
                  <slot name="extra-controls" />
                </td>
                <td v-else :colspan="showHeadings.length">
                  Showing entries from
                  <LocalDateTime :datetime="startTimeRange" /> to
                  <LocalDateTime :datetime="endTimeRange" />.
                  <span v-if="more">
                    <a
                      @click.stop="$emit('download-more', true)"
                      @keyup.enter="$emit('download-more', true)"
                      >Load older results...</a
                    >
                  </span>
                  <a
                    v-if="loading"
                    aria-label="Loading, click to cancel"
                    @click.stop="$emit('download-cancel')"
                    @keyup.enter="$emit('download-cancel')"
                    ><LoadingAnim tag="span" inline
                  /></a>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="dataAvailable" class="results-tbody">
              <tr class="time-range-row">
                <td v-if="nearby" :colspan="showHeadings.length">
                  <slot name="extra-controls" />
                </td>
                <td v-else :colspan="showHeadings.length">
                  No results found with these filters in this date range.
                  <span v-if="filterView">
                    <a @click.stop="resetAllFilters" @keyup.enter="resetAllFilters"
                      >Reset filters</a
                    >
                  </span>
                </td>
              </tr>
            </tbody>
            <tbody v-else-if="loading">
              <tr>
                <td :colspan="showHeadings.length">
                  <LoadingAnim />
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td :colspan="showHeadings.length">
                  <slot name="no-data-message">No results available in this date range.</slot>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="controls-and-csv">
          <TableControls
            v-if="!nearby && bottomControls"
            :page-size.sync="pageSize"
            :page.sync="page"
            :num-entries-filtered="sortedResults.length"
            :num-entries="dataCount"
            :loading="loading"
            :more="more"
            :is-any-filter-applied="isAnyFilterApplied"
            @download-more="$emit('download-more', $event)"
            @download-cancel="$emit('download-cancel', $event)"
          >
            <slot name="extra-controls" />
            <a
              class="btn btn-link"
              @click.stop="checkDownloadCSV()"
              @keyup.enter="checkDownloadCSV()"
            >
              <i class="glyphicon glyphicon-download-alt" />
              Download as CSV
            </a>
          </TableControls>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEqual from 'lodash/isEqual';
import _flatten from 'lodash/flatten';
import _uniq from 'lodash/uniq';
import _uniqBy from 'lodash/uniqBy';
import exportToCsv from '../js/export-csv';
import LocalDateTime from './LocalDateTime.vue';
import TableControls from './TableControls.vue';
import Select2 from './Select2';
import events from '../events';

// Simple hashing function
// eslint-disable-next-line no-bitwise
const hashCode = (s) => s.split('').reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0);

export default {
  name: 'ResponsiveTable',
  components: {
    LocalDateTime,
    Select2,
    TableControls,
  },
  props: {
    columns: {
      type: Array,
      required: true,
    },
    data: {
      type: Array,
      required: true,
    },
    initShowCols: {
      type: Array,
      required: false,
      default: () => [],
    },
    allDataCount: {
      type: Number,
      required: false,
      default: null,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    more: {
      type: Boolean,
      required: false,
      default: false,
    },
    expanding: {
      type: Boolean,
      required: false,
      default: false,
    },
    csvFileName: {
      type: String,
      required: false,
      default: 'API-Results.csv',
    },
    extraCsvData: {
      type: Array,
      required: false,
      default: () => [],
    },
    timeRangeId: {
      type: String,
      required: false,
      default: '',
    },
    expandedIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    nearby: {
      type: String,
      required: false,
      default: '',
    },
    topControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    bottomControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    showColPicker: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultSortCol: {
      type: String,
      required: false,
      default: null,
    },
    defaultSortReversed: {
      type: Boolean,
      required: false,
      default: true,
    },
    initPageSize: {
      type: Number,
      required: false,
      default: 25,
    },
    augmentData: {
      type: Function,
      required: false,
      default: (obj) => obj,
    },
    tableTitle: {
      type: String,
      required: false,
      default: '',
    },
    tableSubtitle: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    const initShowCols = [...this.initShowCols];
    const knownColIds = _uniq(this.columns.map(({ id, groupId }) => groupId || id));
    const defaultColds = initShowCols && initShowCols.length ? initShowCols : knownColIds;
    const tableIdHash = hashCode(knownColIds.join(''));
    const savedCols = JSON.parse(window.localStorage.getItem(`${tableIdHash}-cols`) || '[]') || [];
    const showCols = savedCols && savedCols.length ? savedCols : defaultColds;

    const filterView = JSON.parse(
      window.localStorage.getItem(`${tableIdHash}-filters-visible`) || 'true',
    );
    // Find first sortable column
    const currentSortBy =
      this.defaultSortCol ||
      showCols.find((colId) => {
        const col = this.columns.find(({ id }) => id === colId);
        return col && col.sortable;
      }) ||
      showCols[0];
    const colsWithFilters = this.columns
      .filter(({ filterable }) => filterable)
      .reduce(
        (soFar, { id, initialValue }) => ({
          ...soFar,
          [id]: initialValue !== undefined && initialValue !== null ? [initialValue] : [],
        }),
        {},
      );
    // If column has an initialValue for filter, show.
    if (colsWithFilters) {
      Object.keys(colsWithFilters).forEach((colId) => {
        if (!showCols.includes(colId) && colsWithFilters[colId].length) {
          showCols.push(colId);
        }
      });
    }

    return {
      currentSortBy,
      currentReversed: this.defaultSortReversed,
      showCols,
      colsPickerVisible: false,
      compact: true,
      filterView,
      showTableFilters: true,
      pageSize: this.initPageSize,
      page: 0,
      filters: colsWithFilters,
      numericFilters: this.columns
        .filter(({ numeric }) => numeric)
        .reduce((soFar, { id }) => ({ ...soFar, [id]: '' }), {}),
      filterOpts: this.columns
        .filter(({ filterable }) => filterable)
        .reduce((soFar, { id }) => ({ ...soFar, [id]: [] }), {}),
      expandedIdsLocal: this.nearby ? [this.nearby] : [],
    };
  },
  computed: {
    shouldDisplayFilters() {
      return this.showGroupHeadings.some((heading) => heading.filterable);
    },

    isAnyFilterApplied() {
      const hasStandardFiltersApplied = Object.values(this.filters).some((arr) => arr.length > 0);
      const hasNumericFiltersApplied = Object.values(this.numericFilters).some(
        (value) => value && value !== '',
      );
      return hasStandardFiltersApplied || hasNumericFiltersApplied;
    },

    hasGroupHeadings() {
      return !!this.columns.find(({ groupId }) => groupId);
    },

    groupHeadingIds() {
      return _uniq(this.columns.map(({ id, groupId }) => groupId || id));
    },

    headingsMap() {
      return this.columns.reduce((soFar, col) => {
        const mapping = soFar;
        mapping[col.id] = {
          ...col,
          title: col.title || col.id,
          children: mapping[col.id]?.children,
        };
        if (col.groupId) {
          const parent = mapping[col.groupId] || { ...mapping[col.id] };
          parent.children = [...(parent.children || []), col];
          mapping[col.groupId] = parent;
        }
        return mapping;
      }, {});
    },

    headings() {
      return this.columns.map(({ id }) => this.headingsMap[id]);
    },
    groupHeadings() {
      return this.groupHeadingIds.map((id) => this.headingsMap[id]);
    },
    // subHeadings() {
    //   return this.headings.reduce(
    //     (soFar, col) => {
    //       const heads = soFar;
    //       heads.push(col);
    //       if (col.children && col.children.length) {
    //         heads.push(...col.children);
    //       }
    //       return heads;
    //     },
    //     [],
    //   );
    // },

    toggleableHeadings() {
      return this.groupHeadings.filter(
        ({ type, noColPick = false }) => type !== 'expand' && type !== 'postpend' && !noColPick,
      );
    },
    // List of headings that is a group heading or an individual heading
    showGroupHeadings() {
      return this.groupHeadings.filter(
        ({ id, type }) => type === 'expand' || type === 'postpend' || this.showCols.includes(id),
      );
    },
    // List of headings that is in a group, and is not an individual heading
    showSubHeadings() {
      return this.headings.filter(
        ({ type, groupId }) =>
          type === 'expand' || type === 'postpend' || this.showCols.includes(groupId),
      );
    },
    showHeadings() {
      return this.headings.filter(
        ({ id, type, groupId }) =>
          type === 'expand' || type === 'postpend' || this.showCols.includes(groupId || id),
      );
    },
    filteredData() {
      if (this.nearby) {
        return this.data.filter(({ nearbyIds }) => nearbyIds.includes(this.nearby));
      }

      const cache = {};
      const that = this;

      function getCachedPct(id, percentile) {
        const cacheKey = `${id}_${percentile}`;
        if (cache[cacheKey]) {
          return cache[cacheKey];
        }

        const allVals = that.data.map((obj) => _get(obj, id, 0));
        allVals.sort((a, b) => a - b);

        const [comparator, pctStr] = percentile.split('p');
        const pctVal = parseInt(pctStr, 10) / 100;

        if (comparator === '<') {
          const idx = Math.ceil(allVals.length * pctVal);
          cache[cacheKey] = allVals[idx];
        } else {
          const idx = Math.floor(allVals.length * pctVal);
          cache[cacheKey] = allVals[idx];
        }
        return cache[cacheKey];
      }

      function comparePercentile(id, percentile, row) {
        if (percentile === 'mid') {
          return !comparePercentile(id, '<p25', row) && !comparePercentile(id, '>p75', row);
        }

        const [comparator] = percentile.split('p');
        const compareVal = getCachedPct(id, percentile);
        const val = _get(row, id);

        if (comparator === '<') {
          return val <= compareVal;
        }
        return val >= compareVal;
      }

      return this.data.filter(
        (row) =>
          Object.entries(this.filters).reduce((soFar, [id, filterArr]) => {
            if (!soFar || !filterArr || !filterArr.length) {
              return soFar;
            }
            const column = this.columns.find(({ id: colId }) => id === colId);
            if (column.type === 'tags') {
              const tags = _get(row, id);
              if (_isArray(tags)) {
                return tags.some((tag) => filterArr.includes(tag));
              }
            } else if (column.filterType === 'multi') {
              const tags = column.filterFn(row);
              if (_isArray(tags)) {
                return tags.some((tag) => filterArr.includes(tag));
              }
            }
            return filterArr.includes(`${_get(row, id)}`);
          }, true) &&
          Object.entries(this.numericFilters).reduce((soFar, [id, numericFilter]) => {
            if (!soFar || !numericFilter) {
              return soFar;
            }
            return comparePercentile(id, numericFilter, row);
          }, true),
      );
    },
    startTimeRange() {
      return _get(this.data[this.data.length - 1], this.timeRangeId);
    },
    endTimeRange() {
      return _get(this.data[0], this.timeRangeId);
    },
    sortedResults() {
      const sortByFn = (obj) => this.columnSortMap[this.currentSortBy](obj);
      const nulls = this.filteredData.filter((obj) => sortByFn(obj) === null);
      const vals = this.filteredData.filter((obj) => sortByFn(obj) !== null);
      const collator = new Intl.Collator();
      const reverser = this.currentReversed ? -1 : 1;
      // Was a lambda, but this helps debugging
      const sorter = !this.currentSortByCol.numeric
        ? function _textSorter(a, b) {
            const valA = sortByFn(a);
            const valB = sortByFn(b);
            const out = reverser * collator.compare(valA, valB);
            // console.log('Compare', valA, valB, out);
            return out;
          }
        : function _numSorter(a, b) {
            const valA = sortByFn(a) || 0;
            const valB = sortByFn(b) || 0;
            return reverser * (valA - valB);
          };
      const sorted = vals.sort(sorter);
      // console.log(
      //   this.currentSortBy,
      //   this.currentSortByCol.numeric,
      //   sorted.map((a) => sortByFn(a)),
      // );
      const out = [...sorted, ...nulls];
      return out;
    },
    sortedPagedData() {
      return this.sortedResults
        .slice(this.page * this.pageSize, (this.page + 1) * this.pageSize)
        .map((row) => this.augmentData(row));
    },
    filterableCols() {
      return this.columns.filter(({ filterable }) => filterable);
    },
    columnSortMap() {
      return this.columns.reduce(
        (soFar, { id, sortFn, textFn }) => ({
          ...soFar,
          [id]: sortFn || textFn || ((obj) => this.valueFor(obj, id)),
        }),
        {},
      );
    },
    columnTextMap() {
      return this.columns.reduce(
        (soFar, { id, textFn }) => ({
          ...soFar,
          [id]: textFn || ((obj) => this.valueFor(obj, id)),
        }),
        {},
      );
    },
    dataCount() {
      return this.allDataCount !== null ? this.allDataCount : this.data.length;
    },
    dataAvailable() {
      return this.dataCount > 0;
    },
    knownColIds() {
      return _uniq(this.columns.map(({ id, groupId }) => groupId || id));
    },
    currentSortByCol() {
      return this.columns.find(({ id }) => id === this.currentSortBy);
    },
  },
  watch: {
    filterableCols: {
      deep: true,
      handler(cols) {
        this.filters = cols.reduce((soFar, { id }) => ({ ...soFar, [id]: [] }), {});
        this.numericFilters = cols.reduce((soFar, { id }) => ({ ...soFar, [id]: '' }), {});
        this.filterOpts = cols.reduce((soFar, { id }) => ({ ...soFar, [id]: [] }), {});
      },
    },
    columns: {
      deep: true,
      handler() {
        this.setFilterOpts(this.data);
      },
    },
    data: {
      deep: true,
      handler(newData) {
        this.setFilterOpts(newData);
      },
    },
    nearby(val) {
      if (val) {
        this.expandedIdsLocal = [val];
        this.pageSize = 25;
      }
    },
    expandedIds(val) {
      if (!_isEqual(val.slice().sort(), this.expandedIds.slice().sort())) {
        this.expandedIdsLocal = val;
      }
    },
    expandedIdsLocal(newVal, oldVal) {
      if (!_isEqual(newVal.slice().sort(), oldVal.slice().sort())) {
        this.$emit('update:expanded-ids', newVal);
      }
    },
  },
  mounted() {
    this.setFilterOpts(this.data);
  },
  methods: {
    toggleFilterVisibility() {
      this.filterView = !this.filterView;
      const tableIdHash = hashCode(this.knownColIds.join(''));
      const filterViewKey = `${tableIdHash}-filters-visible`;
      window.localStorage.setItem(filterViewKey, JSON.stringify(this.filterView));

      if (!this.filterView) {
        this.resetAllFilters();
      }
    },
    toggleColumnVisibility() {
      this.colsPickerVisible = !this.colsPickerVisible;
    },
    valueFor(row, id) {
      return _get(row, id, null);
    },
    toggleCol(id) {
      if (this.showCols.includes(id)) {
        this.showCols = this.showCols.filter((col) => col !== id);
      } else {
        this.showCols = [...this.showCols, id];
      }
      const tableIdHash = hashCode(this.knownColIds.join(''));
      const savedCols = JSON.stringify(this.showCols);
      window.localStorage.setItem(`${tableIdHash}-cols`, savedCols);
    },
    setAllCols() {
      this.showCols = [...this.knownColIds];
      const tableIdHash = hashCode(this.knownColIds.join(''));
      const savedCols = JSON.stringify(this.showCols);
      window.localStorage.setItem(`${tableIdHash}-cols`, savedCols);
    },
    resetCols() {
      this.showCols = [...this.initShowCols];
      const tableIdHash = hashCode(this.knownColIds.join(''));
      window.localStorage.removeItem(`${tableIdHash}-cols`);
    },
    onSortClick(id) {
      const reversed = id === this.currentSortBy ? !this.currentReversed : false;
      this.currentSortBy = id;
      this.currentReversed = reversed;
    },
    isExpanded(id) {
      return this.expanding && this.expandedIdsLocal.includes(id);
    },
    setExpanded(id, expand) {
      if (!this.expanding) {
        this.$emit('row-click', id);
        return;
      }
      const ids = this.expandedIdsLocal.filter((exId) => exId !== id);
      if (expand) {
        ids.push(id);
      }
      this.expandedIdsLocal = ids;
    },
    downloadCSV() {
      const headings = this.showHeadings.filter(({ type }) => type !== 'expand');
      const headCols = this.hasGroupHeadings
        ? headings.map(({ groupTitle }) => groupTitle || '')
        : [];
      const columns = headings.map(({ title }) => title);

      return exportToCsv(this.csvFileName, [
        headCols,
        columns,
        ...this.sortedResults.map((row) =>
          headings.map(({ id }) => {
            const val = `${this.columnTextMap[id](row)}`; // enforce strings
            if (val.startsWith('=')) {
              return `\`${val}`;
            }
            return val;
          }),
        ),
        ...this.extraCsvData,
      ]);
    },
    async checkDownloadCSV() {
      if (!this.more) {
        this.downloadCSV();
      } else {
        new Promise((resolve, reject) => {
          events.$emit(events.MODAL_SHOW, {
            title: 'Load more data?',
            body: 'There are more entries available for this date range. Would you like to load the extra data first?',
            cancelText: 'Download CSV now',
            okayText: 'Load extra data',
            callback: (okayed) => {
              if (okayed) {
                resolve(okayed);
              } else {
                reject();
              }
            },
          });
        }).then(
          () => {
            // Success callback
            this.$emit('download-more');
          },
          () => {
            // Reject callback
            this.downloadCSV();
          },
        );
      }
    },
    setFilterOpts(dataIn) {
      this.filterableCols.forEach(({ id, type, filterType, filterFn }) => {
        let opts = null;

        if (type === 'tags') {
          opts = _uniqBy(
            _flatten(
              dataIn.map((row) => {
                const tags = this.valueFor(row, id);
                if (_isArray(tags)) {
                  return tags.map((tag) => ({
                    id: tag,
                    text: tag || '?',
                  }));
                }
                return [];
              }),
            ),
            'id',
          ).sort((a, b) => a.text.localeCompare(b.text));
        } else if (filterType === 'multi') {
          opts = _uniqBy(
            _flatten(
              dataIn.map((row) => {
                const tags = filterFn(row);
                if (_isArray(tags)) {
                  return tags.map((tag) => ({
                    id: tag,
                    text: tag || '?',
                  }));
                }
                return [];
              }),
            ),
            'id',
          ).sort((a, b) => a.text.localeCompare(b.text));
        } else {
          opts = _uniqBy(
            dataIn.map((row) => ({
              id: this.valueFor(row, id),
              text: `${this.columnTextMap[id](row)}`, // enforce strings
            })),
            'id',
          ).sort((a, b) => a.text.localeCompare(b.text));
        }

        if (!_isEqual(this.filterOpts[id], opts)) {
          this.$set(this.filterOpts, id, opts);
        }
      });
    },
    setFilter(colId, filter) {
      if (!this.filters[colId].includes(filter)) {
        this.filters[colId].push(filter);
      }
    },
    resetAllFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = [];
      });

      Object.keys(this.numericFilters).forEach((key) => {
        this.numericFilters[key] = '';
      });
      this.$emit('filters-reset');
    },
  },
};
</script>

<style scoped>
.table-container {
  display: flex;
  width: 100%;
  gap: 8px;
  flex-direction: column;
  margin-bottom: 24px;
}

.clickable-row {
  cursor: pointer;
}
.compact {
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  vertical-align: middle;
}
.table {
  margin-bottom: 0;
  border-collapse: separate !important;
  background: var(--border);
  border-radius: 12px;
  border-spacing: 1px;
  clip-path: inset(1px);
}
.table > tbody > tr > td,
.table > tbody > tr > th,
.table > tfoot > tr > td,
.table > tfoot > tr > th,
.table > thead > tr > td {
  word-break: break-word;
}
.expanded-row {
  background-color: var(--dark-main-alt) !important;
}

.td-content {
  overflow: hidden;
}
.expand-enter-active,
.expand-leave-active {
  transition: opacity 300ms linear;
  opacity: 1;
}
.expand-enter,
.expand-leave-to {
  opacity: 0;
}
.expand-enter-active .td-content,
.expand-leave-active .td-content {
  transition: height 300ms linear;
  height: 220px;
}
.expand-enter .td-content,
.expand-leave-to .td-content {
  height: 0;
}

.badge-enabled {
  background-color: var(--accent);
}
.time-range-row {
  background-color: var(--dark-primary-alt) !important;
  border-top: 2px solid var(--border);
  border-bottom: 2px solid var(--border);
}
.time-range-row td {
  text-align: center;
  font-size: 13px;
  padding: 4px;
}

.table > thead > tr:last-child > th {
  border-bottom: 0px;
}

tr > .td-group-left {
  border-left: 4px solid var(--border);
}

tr > .td-group-right:last-child {
  border-right: 4px solid var(--border);
}

.th-narrow {
  width: 50px;
}

.th-fixed-wide {
  width: 30%;
  min-width: 220px;
}

.active {
  color: var(--white);
}

.column-sort {
  cursor: pointer;
  color: var(--accent);
}

.columns-row {
  background: var(--diag-purple1) !important;
  border-radius: 8px 8px 0px 0px;
}

.colums-row th {
  padding: 0px 12px !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-weight: regular !important;
}

.columns-row th:first-child {
  border-radius: 8px 0px 0px 0px;
}

.filters-row th {
  background: var(--lightpurple);
  border-bottom: none !important;
}

.tr-con {
  display: flex;
  flex-direction: column;
  background: var(--diag-purple1);
  border-radius: 8px;
  max-width: 100%;
  overflow-x: auto;
}

.tr-con tr {
  background: var(--lightpurple);
}

.column-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 24px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
}

.column-btn p {
  margin: 0;
}

.column-btn a {
  padding: 0px !important;
}

.column-btn i {
  font-size: 24px;
  width: 24px;
  height: 24px;
  display: flex !important;
  border-radius: 8px;
}

.table-btns {
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: flex-end;
}

.bts-plus-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.controls-and-csv {
  display: flex;
  flex-direction: row;
  gap: 24px;
}

.filter-visible {
  background: var(--accent);
  border: 1px solid #fff;
}

.filter-hidden {
  background: var(--dark-main-alt);
  border: 1px solid var(--accent);
}

.icon-active {
  color: var(--white);
}

.icon-inactive {
  color: var(--accent);
}

.filter-label {
  font-weight: 500;
}

.sort-tr th {
  background: var(--diag-purple1);
}
</style>

<style>
.filter > .select2-container .select2-selection--single {
  height: 39px !important;
  padding-top: 8px;
}

.select2-container--bootstrap .select2-selection {
  font-size: 14px;
}

.column-toggle {
  display: flex;
  flex-direction: row;
  background: var(--dark-main-alt);
  align-items: center;
  justify-content: center;
  padding: 0px 12px;
  gap: 8px;
  height: 38px;
  border-radius: 8px;
  cursor: pointer;
}

.column-toggle i {
  color: white;
}

.column-toggle p {
  margin: 0;
}

.title-btns {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-headings {
  display: flex;
  flex-direction: column;
}

.table-headings h4 {
  font-size: 24px;
  color: var(--subhead-blue);
  font-weight: 400;
}

.table-headings p {
  color: #939598;
  font-size: 14px;
}
</style>
